import { Link } from 'react-router-dom';
import { Church, ArrowRight } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { AutoTranslate } from '../../hooks/useAutoTranslate';
import { useLanguage } from '../../context/LanguageContext';
import { getChurchWeeklySchedule } from '../../utils/calendarUtils';

// Static church metadata — addresses don't change so they live in code, not the CMS.
const CHURCH_DEFS = [
  {
    nameKey: 'fatimakronaChurch',
    nameFallback: 'Vår Fru av Fatima kyrka',
    locationKey: 'karlskrona',
    address: 'Södra Kungsgatan 1',
    postcode: '371 31 Karlskrona',
    image: 'https://customer-assets.emergentagent.com/job_758a8f60-e974-403f-a992-2e40a1d6338f/artifacts/hbbpp13g_Fatimakrona.jpg',
    code: 'karlskrona',
  },
  {
    nameKey: 'stBonifatiusChurch',
    nameFallback: 'S:t Bonifatius kyrka',
    locationKey: 'karlshamn',
    address: 'N.Fogdelyckegatan 26',
    postcode: '374 38 Karlshamn',
    image: 'https://customer-assets.emergentagent.com/job_758a8f60-e974-403f-a992-2e40a1d6338f/artifacts/1ttzserr_St%20Bonifatius.jpg',
    code: 'karlshamn',
  },
  {
    nameKey: 'stAntoniusChurch',
    nameFallback: 'S:t Antonius kyrka',
    locationKey: 'olofstrom',
    address: 'Bruksgatan 8',
    postcode: '293 38 Olofström',
    image: 'https://customer-assets.emergentagent.com/job_758a8f60-e974-403f-a992-2e40a1d6338f/artifacts/fbsfqxbg_At%20Antonius.jpg',
    code: 'olofstrom',
  },
];

// Three-column church grid showing this week's mass schedule per location.
const ChurchesSection = ({ weeklyCalendar }) => {
  const { t, language } = useLanguage();

  const churches = CHURCH_DEFS.map((c) => ({
    ...c,
    weeklySchedule: getChurchWeeklySchedule(weeklyCalendar[c.code], language),
  }));

  return (
    <section className="py-20" data-testid="churches-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-serif font-bold text-gray-900 mb-4">{t('ourChurches')}</h2>
          <p className="text-lg text-gray-600">{t('threeChurchesOneCommunity')}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {churches.map((church) => (
            <Card key={church.code} className="overflow-hidden card-hover border-0 shadow-lg">
              <div className="h-48 overflow-hidden">
                <img src={church.image} alt={t(church.nameKey) || church.nameFallback} className="w-full h-full object-cover" />
              </div>
              <CardContent className="p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <Church className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-xl font-serif font-bold text-gray-900 mb-1">
                      {t(church.nameKey) || church.nameFallback}
                    </h3>
                    <p className="text-sm text-gray-500">{church.address}</p>
                    <p className="text-sm text-gray-500">{church.postcode}</p>
                  </div>
                </div>
                <h4 className="text-sm font-semibold text-gray-800 mb-2 border-b pb-1">
                  <AutoTranslate>Denna veckas liturgi</AutoTranslate>
                </h4>
                <div className="space-y-3 mb-4" data-testid={`church-weekly-schedule-${church.code}`}>
                  {church.weeklySchedule.length === 0 ? (
                    <p className="text-sm text-gray-500" data-testid={`church-weekly-empty-${church.code}`}>
                      <AutoTranslate>Inget schema denna vecka</AutoTranslate>
                    </p>
                  ) : (
                    church.weeklySchedule.map((dayGroup, idx) => (
                      <div
                        key={`${church.code}-${dayGroup.dateLabel}-${idx}`}
                        className="text-sm"
                        data-testid={`church-weekly-day-${church.code}-${idx}`}
                      >
                        <div className="font-medium text-gray-900 mb-1 flex items-center gap-2">
                          <span className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />
                          {dayGroup.dateLabel}
                        </div>
                        <div className="pl-4 space-y-0.5">
                          {dayGroup.items.map((item, itemIdx) => (
                            <div
                              key={`${item.time}-${item.title?.slice(0, 10) || itemIdx}`}
                              className="flex items-center gap-2 text-gray-600"
                            >
                              <span className="font-mono text-xs w-12">{item.time}</span>
                              <span className="text-gray-400">-</span>
                              <span><AutoTranslate>{item.title}</AutoTranslate></span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <Link to={`/liturgi?church=${church.code}`}>
                  <Button variant="outline" size="sm" className="w-full">
                    {t('seeEvents') || 'Se övrigt liturgi'}
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ChurchesSection;
